// Shot Map — a player's shots on a rotated pitch, ported from js/renderers-shotmap.js
// (renderShotMapModule, drawShots, wireShotSelection, renderShotTable). The shared shell —
// strip, stats bar, empty states, chips, Colour-by, table toggle — is EventMapBody (the
// legacy renderEventModule, shared with Pass Map); this file is Shot Map's cfg, pitch and table.
//
// needsPlayer:true, not mergeable. Pattern: JSX marks over the pure shotmap-view.ts (the
// legacy rebuilds innerHTML per render, no transitions) + ONE pointer effect for what must
// not re-render React on every mousemove:
// - hover is magnetic (distance to each marker's edge — hollow markers can't be hit-tested
//   natively), so the effect toggles `.event-shot-hovered` + the shared tooltip directly;
// - a click LOCKS a shot. That's rare, so it IS React state (04l-d fork 2A): the lock lives
//   on `mod._eventSelectedId` (so it survives any re-render — a filter, a zoom, undo) and
//   the freeze frame, the hidden-others classes and the details panel are plain JSX.
//
// Drag-to-zoom: the shared usePitchAreaZoom hook (04l-d-2) writes `_eventAreaSelection`;
// everything else renders from that state (the zoomed view, the narrowed stats/table, both
// Reset view buttons), so undo/preset restore a zoom for free. The hover/click guards read
// the gesture's legacy DOM flags (dataset.areaPointerDown / areaDragging).
//
// Instance state (the legacy keys, so saveState/undo capture them): `_eventFilters` (shared
// with Pass Map + Passing Profile), `_eventMapView` ('table' | 'vis'), `_shotColorBy`,
// `_eventSelectedId`, `_eventAreaSelection`, and the kit's `_eventMapCompetitionId`/`_eventMapSeason`.

import { useEffect, useMemo, useReducer, useRef } from "react";
import type { ProtoEvent } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { LIVE } from "../live/flag.js";
import { TeamMap, showsTeamEvents } from "./TeamMap.js";
import { EventMapModule, useGradientPrefix, type EventModuleConfig, type EventPitchProps } from "./EventMapBody.js";
import { EventDetailLayout } from "./EventDetailParts.js";
import { ShotDetailsPanel } from "./ShotDetailsPanel.js";
import { PitchMarkings } from "./PitchMarkings.js";
import { type SortColumn } from "./sortable-rows.js";
import { useSortableRows } from "./useSortableRows.js";
import { usePitchAreaZoom } from "./usePitchAreaZoom.js";
import {
  SHOT_COLOR_MODES,
  SHOT_MARKER_RADIUS,
  freezeFrameDots,
  freezeFrameOf,
  nextHoveredShot,
  shotAtClick,
  shotGeometry,
  shotMarkerColor,
  shotStats,
  shotTableRows,
  shotTooltip,
  type ShotTableRow,
} from "./shotmap-view.js";
import { paintOrder, pitchZoomView, shotFilters, type EventContext } from "../engines/event-map-kit.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";
import { noteShotMatch } from "./XgStory.js";

const SHOT_CFG: EventModuleConfig = {
  key: "shots",
  live: true, // real data on ?live=1 (T2-6)
  emptyNoun: "shot",
  pluralNoun: "shots",
  filters: shotFilters,
  colorModes: SHOT_COLOR_MODES,
  colorStateKey: "_shotColorBy",
  defaultColor: "outcome",
  stats: (filtered, events) => {
    const stats = shotStats(filtered, events);
    return (
      <>
        <div className="event-stat"><span className="event-stat-value">{stats.shown}</span><span className="event-stat-label">shots shown</span></div>
        <div className="event-stat"><span className="event-stat-value">{stats.npXg}</span><span className="event-stat-label">non-pen xG</span></div>
        <div className="event-stat"><span className="event-stat-value">{stats.xgPerShot}</span><span className="event-stat-label">xG / shot</span></div>
        <div className="event-stat"><span className="event-stat-value">{stats.goals}</span><span className="event-stat-label">goals</span></div>
        <div className="event-stat"><span className="event-stat-value">{stats.penalties}</span><span className="event-stat-label">penalties</span></div>
      </>
    );
  },
  Pitch: ShotPitch,
  Table: ShotTable,
};

export function ShotMapModule(props: ProtoModuleProps) {
  // A team on it, or a match dropped on it (teams and matches, Ben 2026-10-30): the team's shots.
  if (LIVE && showsTeamEvents(props.mod)) return <TeamMap {...props} cfg={SHOT_CFG} />;
  return <EventMapModule {...props} cfg={SHOT_CFG} />;
}

// --- the pitch + details panel --------------------------------------------------------------

interface ShotPoint {
  shot: ProtoEvent;
  x: number;
  y: number;
  ex: number;
  ey: number;
  radius: number;
  rgb: string;
  tooltip: string;
}

function ShotPitch({ mod, ctx, events: shots, colorMode, area, onArea, below }: EventPitchProps) {
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  const gradPrefix = useGradientPrefix();
  const svgRef = useRef<SVGSVGElement>(null);
  usePitchAreaZoom(svgRef, onArea);

  // Extreme values paint last (on top) for a continuous mode; categorical modes keep the
  // data's chronological order.
  const points = useMemo<ShotPoint[]>(
    () =>
      paintOrder(shots, colorMode).map((shot) => {
        const g = shotGeometry(shot);
        return { shot, ...g, radius: SHOT_MARKER_RADIUS, rgb: shotMarkerColor(shot, colorMode), tooltip: shotTooltip(ctx, shot) };
      }),
    [shots, colorMode, ctx],
  );

  // The lock is restored by id on every render; a shot the current filters/zoom exclude
  // just shows unlocked (the id is kept, as the legacy keeps it, so it comes back if the
  // shot does).
  const selectedId = getModState<string | null>(mod, "_eventSelectedId");
  const selected = selectedId != null ? (points.find((pd) => pd.shot.id === selectedId) ?? null) : null;

  const shotsRef = useRef(shots);
  shotsRef.current = shots;
  // The pointer effect reads the latest lock + select action without re-wiring per render.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const selectRef = useRef((id: string | null) => {
    setModState(mod, "_eventSelectedId", id);
    // Live: its match lights up on the xG story above (Ben, 2026-10-20).
    noteShotMatch(mod, "select", id === null ? null : (shotsRef.current.find((s) => s.id === id)?.match_id ?? null));
    hideTooltip();
    forceRender();
  });

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    // The CTM must come from the rotated group itself — the points live in its local space.
    const rotateGroup = svg.querySelector<SVGGElement>(".pitch-rotate");
    const layer = svg.querySelector(".event-layer");
    if (!rotateGroup || !layer) return;
    // React renders the groups in `points` order, so child i is point i.
    const entries = points.map((pd, i) => ({ ...pd, el: layer.children[i] as SVGGElement }));
    let hovered: (typeof entries)[number] | null = null;

    const localPoint = (e: MouseEvent) => {
      const ctm = rotateGroup.getScreenCTM();
      if (!ctm) return null;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      return pt.matrixTransform(ctm.inverse());
    };
    const setHovered = (entry: typeof hovered, e?: MouseEvent) => {
      if (hovered !== entry) {
        hovered?.el.classList.remove("event-shot-hovered");
        hovered = entry;
        hovered?.el.classList.add("event-shot-hovered");
        noteShotMatch(mod, "hover", entry ? entry.shot.match_id : null);
      }
      if (entry && e) showTooltipAt(entry.tooltip, e.clientX, e.clientY, "below");
      else hideTooltip();
    };

    const onMove = (e: MouseEvent) => {
      // No hover while an area-zoom drag is pending or live (usePitchAreaZoom's DOM flags).
      if (svg.dataset.areaDragging === "1" || svg.dataset.areaPointerDown === "1") return;
      // Locked: hover does nothing — the only live interaction is the freeze-frame players.
      if (selectedRef.current) {
        if (hovered) setHovered(null);
        return;
      }
      const p = localPoint(e);
      if (!p) return;
      setHovered(nextHoveredShot(p, entries, hovered), e);
    };
    const onLeave = () => setHovered(null);
    const onClick = (e: MouseEvent) => {
      // The click that ends an area drag means "finish the rectangle", not "pick a shot".
      if (svg.dataset.areaDragging === "1") return;
      const p = localPoint(e);
      if (!p) return;
      const hit = shotAtClick(p, entries);
      // A freeze-frame player often sits right on top of the locked shot, so the distance
      // hit-test wins first; only a miss that lands on a player is "just inspecting".
      if (!hit && (e.target as Element).closest(".freeze-frame-dot")) return;
      // While locked, any other click unlocks (select is a separate second click).
      if (selectedRef.current) {
        selectRef.current(null);
        return;
      }
      if (hit) selectRef.current(hit.shot.id);
    };

    svg.addEventListener("mousemove", onMove);
    svg.addEventListener("mouseleave", onLeave);
    svg.addEventListener("click", onClick);
    return () => {
      svg.removeEventListener("mousemove", onMove);
      svg.removeEventListener("mouseleave", onLeave);
      svg.removeEventListener("click", onClick);
      hovered?.el.classList.remove("event-shot-hovered");
    };
  }, [points, mod]);

  const zoom = pitchZoomView(area);
  const groupClass = (pd: ShotPoint) =>
    `event-shot-group${selected === pd ? " event-shot-selected" : selected ? " event-shot-hidden" : ""}`;

  const pitch = (
    <div className="event-pitch-wrap">
      <svg
        ref={svgRef}
        className={`event-pitch-svg event-pitch-svg-rotated${zoom.zoomed ? " event-zoomed" : ""}`}
        viewBox={zoom.viewBox}
        preserveAspectRatio="xMidYMid meet"
        // Inline only while zoomed, so the stylesheet's static 74:114 rule wins otherwise.
        style={zoom.aspectRatio ? { aspectRatio: zoom.aspectRatio } : undefined}
      >
        <defs className="event-defs">
          {/* A per-line gradient (faint at the shot, fuller at its end) shows direction
              without an arrowhead — the same one Pass Map's lines use. */}
          {points.map((pd, i) => (
            <linearGradient key={pd.shot.id} id={`${gradPrefix}-${i}`} gradientUnits="userSpaceOnUse" x1={pd.x} y1={pd.y} x2={pd.ex} y2={pd.ey}>
              <stop offset="0%" stopColor={pd.rgb} stopOpacity="0.04"></stop>
              <stop offset="100%" stopColor={pd.rgb} stopOpacity="0.75"></stop>
            </linearGradient>
          ))}
        </defs>
        <g className="pitch-rotate" transform={zoom.transform}>
          <g className="pitch-frame">
            <PitchMarkings />
          </g>
          <g className="event-layer">
            {points.map((pd, i) => (
              <g key={pd.shot.id} className={groupClass(pd)}>
                <line className="event-shot-line" x1={pd.x} y1={pd.y} x2={pd.ex} y2={pd.ey} stroke={`url(#${gradPrefix}-${i})`}></line>
                <circle cx={pd.x} cy={pd.y} r={pd.radius} className="event-shot-marker" style={{ fill: pd.rgb, stroke: pd.rgb }}></circle>
              </g>
            ))}
          </g>
          <g className="freeze-frame-layer">
            {/* Every other player at the moment of the locked shot, facing the ball. Their
                identity tooltips are the declarative data-tooltip path (well-separated
                shapes, so native hover is reliable here). */}
            {selected &&
              freezeFrameDots(selected, freezeFrameOf(selected.shot)).map((d, i) => (
                <g key={i} className={d.className} transform={`translate(${d.x} ${d.y}) rotate(${d.angle})`} data-tooltip={d.tooltip}>
                  <rect className="freeze-frame-body" x="-1.3" y="-0.55" width="2.6" height="1.1" rx="0.55"></rect>
                  <circle className="freeze-frame-head" cx="0" cy="0" r="0.75"></circle>
                </g>
              ))}
          </g>
        </g>
      </svg>
      {area && (
        <button type="button" className="event-area-reset-btn" onClick={() => onArea(null)}>
          Reset view
        </button>
      )}
    </div>
  );

  return (
    <EventDetailLayout pitch={pitch} header="Shot Details" mod={mod} below={below}>
      {selected ? <ShotDetailsPanel ctx={ctx} shot={selected.shot} /> : <div className="event-details-empty">Click a shot to see its details</div>}
    </EventDetailLayout>
  );
}

// --- the table ----------------------------------------------------------------------------

// Opposition + Outcome are the legacy's `.no-sort` columns; Min (by its data-val minute)
// and xG sort numerically.
const SHOT_COLUMNS: SortColumn<ShotTableRow>[] = [
  { label: "Opposition", get: (r) => r.opposition, sortable: false },
  { label: "Min", get: (r) => r.minute },
  { label: "Outcome", get: (r) => r.outcome, sortable: false },
  { label: "xG", get: (r) => r.xg },
];

function ShotTable({ ctx, events: shots }: { ctx: EventContext; events: ProtoEvent[] }) {
  const source = useMemo(() => shotTableRows(ctx, shots), [ctx, shots]);
  const { rows, headerProps, rankProps, containerRef } = useSortableRows(source, SHOT_COLUMNS);
  return (
    <div className="data-wrapper top-align event-table" ref={containerRef}>
      <div className="data-row header">
        <div className="col-rank" onClick={rankProps.onClick}>#</div>
        <div className="col-team no-sort">Opposition</div>
        <div className={`col-num ${headerProps(1).className}`.trim()} onClick={headerProps(1).onClick}>Min</div>
        <div className="col-team no-sort">Outcome</div>
        <div className={`col-num ${headerProps(3).className}`.trim()} onClick={headerProps(3).onClick}>xG</div>
      </div>
      {rows.map((r, i) => (
        <div key={r.key} data-sort-key={r.key} className="data-row">
          <div className="col-rank">{i + 1}</div>
          <div className="col-team">{r.opposition}</div>
          <div className="col-num" data-val={r.minute}>{r.clock}</div>
          <div className="col-team">{r.outcome}</div>
          <div className="col-num">{r.xg}</div>
        </div>
      ))}
    </div>
  );
}
